import React, { useEffect, useRef, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoadMaintenance } from '../../../../../../services/types/Calculator';
import { Button, Modal } from '../../../../../../shared/components';
import { calculateRoadMaintenance } from '../../Utils/moduleCalculates';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import RoadMaintenanceDetails from './Details/RoadMaintenanceDetails';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  roadMaintenance: RoadMaintenance;
  onUpdateRoadMaintenance: (updatedRoadMaintenance: RoadMaintenance) => void;
}

const RoadMaintenanceSection: React.FC<Props> = ({ roadMaintenance, onUpdateRoadMaintenance }) => {
  const { t } = useTranslation();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(null);
  const [landUseInputValue, setLandUseInputValue] = useState<string>(''); // Valor inicial como cadena vacía
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(true);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const areaFromRoad = roadMaintenance.areaFromPavement || 0;
    if (areaFromRoad > 0 && roadMaintenance.isCustomArea === false) {
      setLandUseInputValue(areaFromRoad.toString());
    }

    if (roadMaintenance.areaFromPavement === null && !roadMaintenance.isCustomArea) {
      setLandUseInputValue('');
      onUpdateRoadMaintenance({
        ...roadMaintenance,
        area: null,
        moduleId: 0,
        subTotal: null,
      });

      setTotalCarbonSum(null);
    }
    const areaNew = roadMaintenance.isCustomArea ? Number(roadMaintenance.area) : areaFromRoad;

    if (areaNew > 0) {
      const emissionFactor = roadMaintenance.factorValue;
      const result = calculateRoadMaintenance(emissionFactor, areaNew);

      setLandUseInputValue(areaNew.toString());
      setTotalCarbonSum(result);
    }
  }, [roadMaintenance.area, roadMaintenance.areaFromPavement]);

  useEffect(() => {
    if (!isInputDisabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isInputDisabled]);

  const handleInputArea = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    const numericValue = value === '' ? null : Number(value);

    setLandUseInputValue(value);

    onUpdateRoadMaintenance({
      ...roadMaintenance,
      area: numericValue,
      subTotal:
        numericValue !== null
          ? calculateRoadMaintenance(roadMaintenance.factorValue, numericValue)
          : null,
    });

    setTotalCarbonSum(
      numericValue !== null
        ? calculateRoadMaintenance(roadMaintenance.factorValue, numericValue)
        : null
    );
  };

  const setCustomArea = () => {
    onUpdateRoadMaintenance({ ...roadMaintenance, isCustomArea: true });
    setIsInputDisabled(false);
  };

  return (
    <>
      <div className="content-calculator">
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Road Area to be Maintained (sq meters)')}
              {roadMaintenance.areaFromPavement && (
                <div className="calculator-default-roadMaintenance">
                  (Default Road Area,{' '}
                  <NumberFormatterSinSeparador value={roadMaintenance.areaFromPavement} />)
                </div>
              )}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              ref={inputRef}
              type="number"
              id="arearoadInput"
              name="areaInput"
              value={landUseInputValue}
              min="0"
              max={99999999.99}
              maxLength={10}
              className="calculator-input"
              disabled={isInputDisabled}
              onChange={handleInputArea}
              onBlur={() => setIsInputDisabled(true)}
              title="Area to be Maintained"
              onKeyDown={handleKeyDown}
            />
            <UncontrolledTooltip
              className="tooltip-toolkit"
              placement="bottom"
              target="arearoadInput"
              modifiers={tooltipPreventOverflowModifiers}
            >
              {t('Area to be Maintained')}
            </UncontrolledTooltip>
          </div>
          {isInputDisabled && (
            <>
              <button
                title="Edit"
                type="button"
                className="gi-icon"
                onClick={setCustomArea}
                id="editIcon_road"
              >
                <FontAwesomeIcon icon="edit" />
              </button>
              <UncontrolledTooltip
                className="tooltip-calculator"
                placement="bottom"
                target="editIcon_road"
                modifiers={tooltipPreventOverflowModifiers}
              >
                {t('Custom Road Area')}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      </div>
      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered  action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          {totalCarbonSum !== null && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId="tr-street-detail"
                    className="btn-tertiary no-padding action-calculator"
                    icon="clipboard-list"
                    text=""
                    onClick={() => setShowDetailsModal(true)}
                  />
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
      <Modal
        title={roadMaintenance.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <RoadMaintenanceDetails
            roadMaintenance={roadMaintenance}
            defaultArea={roadMaintenance.areaFromPavement}
          />
        </div>
      </Modal>
    </>
  );
};

export default RoadMaintenanceSection;
